/**
 * checkoutErrorText — ЕДИНЫЙ текст покупателю, когда оплата не началась
 * (WEB-933 п.2).
 *
 * Раньше этот маппинг был скопирован в трёх местах — BuyNowButton,
 * DownsellCard (PublicBlockRendererExtended), WalletsExpressButtons
 * (PublicBlockRenderer) — с разными формулировками и разной полнотой веток.
 * Покупатель мог прочитать «No Stripe price configured for this button.»,
 * «Stripe not configured for this project» или «Network Error», а ветки
 * `required-fields` не было вовсе — она падала в «Failed to start checkout».
 *
 * Правило: наружу уходит текст, написанный для покупателя. Наши внутренности
 * (Stripe, ключи, настройки проекта) покупателя не касаются — это ещё и
 * гигиена money-пути (шрам WEB-595: сырой текст платёжного SDK утёк в ответ).
 */
import type { CheckoutResult } from '@/components/editor/paywallCheckoutContext';
import { getPublicQuizStrings } from '@/utils/publicQuizStrings';

/**
 * WEB-1820 (возврат Д21 13.09) — ОТКАЗ СЕРВЕРА НА ЯЗЫКЕ ВОРОНКИ.
 *
 * Раньше здесь стояла эвристика `isBuyerFacing`: «в сообщении сервера есть
 * кириллица → значит, оно писано покупателю, показываем как есть». Она верно
 * отличала наши внутренности от текста человеку, но про ЯЗЫК не знала
 * ничего — у сервера он один (русский литерал в `message`), а у воронок 31
 * язык. Живой прогон QA: на АНГЛИЙСКОМ пейволле под кнопкой «Buy Now»
 * появилось «Недопустимый адрес возврата.».
 *
 * Теперь ветвление идёт по МАШИННОМУ КОДУ ответа (его сохраняет перехватчик
 * `api/client.ts`), а текст берётся из словаря воронки. Подбор по тексту
 * невозможен принципиально: первая же правка формулировки на сервере молча
 * ломает подбор (тот же довод — в шапке `backend/.../human-error-codes.ts`).
 * Кабинетный `localizeError` тут не годится: он отвечает на языке КАБИНЕТА
 * (`getUiLocale`) и знает только пару ru/en.
 *
 * ВТОРОЙ КОНЕЦ правила (CLAUDE.md, WEB-1844) — множество кодов, которые касса
 * вообще умеет показать покупателю:
 * `backend/src/paywalls/utils/checkout-buyer-codes.web1820.json`. Сторожа
 * сверяют множества в обе стороны: `checkoutErrorText.twoEnds.web1820.test.ts`
 * (фронт) и `checkout-buyer-codes.web1820.spec.ts` (бэк, выводит коды обходом
 * исходников — чтобы новый код не проехал молча).
 */

/** Ключи словаря воронки, которыми отвечает касса. */
type CheckoutStringKey =
  | 'checkoutUnavailable'
  | 'checkoutPriceUnavailable'
  | 'checkoutContactSupport'
  | 'checkoutStartFailed';

export const CHECKOUT_SERVER_CODE_STRING_KEYS = {
  PAYMENTS_UNAVAILABLE: 'checkoutUnavailable',
  // Цены на странице больше нет (снята у продавца либо не принадлежит этой
  // странице): «попробуйте ещё раз» здесь врёт — повтор даст тот же отказ.
  PRICE_GONE: 'checkoutPriceUnavailable',
  PRICE_NOT_ON_PAYWALL: 'checkoutPriceUnavailable',
  // Идентификатор покупателя не передан или принадлежит другой организации:
  // покупатель этого не чинит, деньги не списаны — дорога в поддержку.
  VISITOR_ID_REQUIRED: 'checkoutContactSupport',
  VISITOR_ID_FOREIGN_PROJECT: 'checkoutContactSupport',
  // Сбой на нашей стороне. Какой именно — остаётся в журналах сервера:
  // покупателю эта причина не адресована (гигиена money-пути, шрамы WEB-595
  // и WEB-816), совет один — попробовать ещё раз.
  CHECKOUT_START_FAILED: 'checkoutStartFailed',
  RETURN_URL_INVALID: 'checkoutStartFailed',
} as const satisfies Record<string, CheckoutStringKey>;

/**
 * Текст для показа рядом с кнопкой; `null` — показывать нечего
 * (успех: раннер уже уводит на страницу оплаты).
 */
/**
 * WEB-967: тексты — из словаря по языку ВОРОНКИ (раньше были русскими для
 * всех). WEB-1820: сообщение сервера наружу не проходит ни в каком виде —
 * язык выбирает воронка, а не язык литерала на сервере.
 */
export function checkoutErrorText(
  result: CheckoutResult,
  language?: string | null,
): string | null {
  if (result.ok) return null;
  const strings = getPublicQuizStrings(language);

  switch (result.error) {
    case 'payments-unavailable':
      // Про «платёжку не подключили / отключили после публикации» покупателю
      // сообщать нечего, кроме факта: чинить это не ему.
      return strings.checkoutUnavailable;
    case 'preview':
      // Единственный случай, где читатель — не покупатель: автор в редакторе
      // либо получатель ссылки предпросмотра (WEB-2115). Фраза одна на обоих,
      // поэтому к действиям автора («опубликуйте») не зовёт.
      return strings.checkoutPreviewOnly;
    case 'no-price':
      return strings.checkoutNoPrice;
    case 'chunk-unavailable':
      // Свежий взгляд пачки 31, п.1: общий «попробуйте ещё раз» здесь врёт —
      // повтор нажатия не поможет, пока страница не перезагружена.
      return strings.checkoutReloadNeeded;
    case 'in-flight':
      // Свежий взгляд пачки 31, п.5: первый запрос ещё идёт — это не отказ,
      // и пугать покупателя красной плашкой не за что.
      return null;
    case 'required-fields':
      // WEB-916 п.10: галочка согласия и незаполненные поля — разные советы.
      return result.requiredKind === 'consent'
        ? strings.checkoutAcceptTerms
        : strings.checkoutFillRequired;
    default: {
      // WEB-1820: известный код сервера → своя строка словаря воронки;
      // неизвестный (новый код, обрыв сети, ответ без кода) → общий текст на
      // ТОМ ЖЕ языке. Русский литерал `result.message` наружу не уходит
      // никогда: он написан на одном языке, а воронок — тридцать один.
      const key = result.code
        ? (CHECKOUT_SERVER_CODE_STRING_KEYS as Record<
            string,
            CheckoutStringKey | undefined
          >)[result.code]
        : undefined;
      return key ? strings[key] : strings.checkoutStartFailed;
    }
  }
}
